<script setup lang="ts">
import { getPetName, getPetPrice, sortPetList } from '../data'
import { useUserStore } from '@/stores/modules/user'
import PetInfo from './PetInfo.vue'
import { ref, computed } from 'vue'
import type { PetType } from '@/types/pet'
import { socketEmitAsync } from '@/utils/web-socket'
import PetCompare from './PetCompare.vue'
import { Lock, QuestionFilled } from '@element-plus/icons-vue'
import { batchSellQualityList } from '../data'

const userStore = useUserStore()

const petInfo = ref<PetType>()
const petList = computed(() => {
  return userStore.petList.filter((item) => {
    if (item.id === userStore.fightPetId) return false
    if (userStore.outsidePetIdList.includes(item.id)) return false
    return true
  })
})
const fightPet = computed(() => {
  return userStore.petList.find((item) => item.id === userStore.fightPetId)
})
const outsidePetIdList = computed(() => {
  return userStore.outsidePetIdList.map((item) => userStore.petList.find((pet) => pet.id === item))
})

async function handleArrange() {
  await socketEmitAsync({ type: 'arrangePet' })
  sortPetList(userStore.petList)
  ElMessage.success('整理成功')
}

const isInItems = ref(false)
function showItemDetail(item: PetType | undefined, openType: boolean) {
  petInfo.value = item
  isInItems.value = openType
}

const compareShow = ref(false)
function handleCompareClose() {
  compareShow.value = false
  petInfo.value = undefined
}

function handleBatchSell({ name, key }: { name: string; key: string }) {
  let filterQuality: string[] = []
  if (key === 'normal') {
    filterQuality = ['normal']
  } else if (key === 'race') {
    filterQuality = ['normal', 'race']
  } else if (key === 'epic') {
    filterQuality = ['normal', 'race', 'epic']
  }
  ElMessageBox.confirm(
    `<p class="affix">确定出售<span class="${key}">${name}及以下</span>品质的魔物吗？（<b style="color: red">操作无法回退，请慎重操作</b>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const equippedPetIdList = userStore.petList
        .filter((item) => item.id === userStore.fightPetId || userStore.outsidePetIdList.includes(item.id))
        .map((item) => item.id)
      const petList = userStore.petList.filter((item) => filterQuality.includes(item.quality) && !equippedPetIdList.includes(item.id) && !item.isLock)
      const idList = petList.map((item) => item.id)

      await socketEmitAsync({ type: 'sellPet', data: { idList } })
      for (const id of idList) {
        const index = userStore.petList.findIndex((item) => item.id === id)
        if (index === -1) continue
        const petInfo = userStore.petList[index]
        const price = getPetPrice(petInfo.quality)
        userStore.gold += price
        userStore.petList.splice(index, 1)
      }
      ElMessage.success('出售成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="pet-item">
    <div class="action">
      <div class="in-use">
        <div class="fight border-wrap">
          <h4>
            出战中
            <div class="help-info" style="margin-left: 5px">
              <el-popover placement="bottom" width="100%" trigger="click">
                <template #reference>
                  <el-icon size="18"><QuestionFilled /></el-icon>
                </template>
                <div class="affix">
                  <h4>魔物出战说明</h4>
                  <p>1. 魔物<span class="info">出战</span>时，您会获得其<span class="equipped">出战属性</span>的基础属性加成。</p>
                  <p>2. 通过<span class="info">绑定套路</span>，您可以在切换装备套路时，同步携带对应宠物。</p>
                  <p>3. 出战属性的上限评分受到品质/阶级影响，如表格所示：</p>
                  <table border style="text-align: center">
                    <tr>
                      <td>上限参照</td>
                      <td class="normal">普通</td>
                      <td class="race">稀有</td>
                      <td class="epic">史诗</td>
                      <td class="legend">传说</td>
                      <td class="antiquity">远古</td>
                    </tr>
                    <tr>
                      <td>1阶</td>
                      <td>6</td>
                      <td>11</td>
                      <td>16</td>
                      <td>21</td>
                      <td>26</td>
                    </tr>
                    <tr>
                      <td>2阶</td>
                      <td>8</td>
                      <td>13</td>
                      <td>18</td>
                      <td>23</td>
                      <td>28</td>
                    </tr>
                    <tr>
                      <td>3阶</td>
                      <td>10</td>
                      <td>15</td>
                      <td>20</td>
                      <td>25</td>
                      <td>30</td>
                    </tr>
                    <tr>
                      <td>···</td>
                      <td>···</td>
                      <td>···</td>
                      <td>···</td>
                      <td>···</td>
                      <td>···</td>
                    </tr>
                  </table>
                </div>
              </el-popover>
            </div>
          </h4>
          <el-button
            :type="fightPet ? 'primary' : 'info'"
            size="small"
            plain
            :style="{ borderColor: fightPet && fightPet.id === petInfo?.id ? 'red' : '' }"
            @click="showItemDetail(fightPet, false)"
          >
            <span v-if="fightPet" :class="fightPet.quality">{{ getPetName(fightPet.monsterId) }}</span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
        <div class="outside border-wrap">
          <h4>
            外出中
            <div class="help-info" style="margin-left: 5px">
              <el-popover placement="bottom" width="100%" trigger="click">
                <template #reference>
                  <el-icon size="18"><QuestionFilled /></el-icon>
                </template>
                <div class="affix">
                  <h4>魔物外出说明</h4>
                  <p>
                    1. 魔物<span class="info">外出</span>时，您在每日0点刷新后进入<span class="pet">魔物牧场</span>，将自动获取其<span
                      class="equipped"
                      >外出属性</span
                    >中产出的物品。
                  </p>
                  <p>2. 外出属性的金币收益上限仅受品质影响，如表格所示：</p>
                  <table border style="text-align: center">
                    <tr>
                      <td class="normal">普通</td>
                      <td class="race">稀有</td>
                      <td class="epic">史诗</td>
                      <td class="legend">传说</td>
                      <td class="antiquity">远古</td>
                    </tr>
                    <tr>
                      <td>6万</td>
                      <td>8万</td>
                      <td>10万</td>
                      <td>15万</td>
                      <td>30万</td>
                    </tr>
                  </table>
                </div>
              </el-popover>
            </div>
          </h4>
          <el-button
            v-for="item of outsidePetIdList"
            :type="item ? 'primary' : 'info'"
            size="small"
            plain
            :style="{ borderColor: item && item.id === petInfo?.id ? 'red' : '' }"
            @click="showItemDetail(item, false)"
          >
            <span v-if="item" :class="item.quality">{{ getPetName(item.monsterId) }}</span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
        <UserAttr style="padding-bottom: 0" />
      </div>
      <div class="detail-info border-wrap">
        <h4>魔物信息</h4>
        <template v-if="petInfo">
          <PetInfo :pet-info="petInfo" :is-in-items="isInItems" @clear="petInfo = undefined" @compare="compareShow = true" />
        </template>
      </div>
    </div>

    <div class="pet-list border-wrap">
      <h4>魔物列表</h4>

      <div class="list-action">
        <span style="margin-right: 10px; font-size: 12px">
          容量：
          <span :style="{ color: userStore.petList.length >= userStore.getPetCapacity - 3 ? 'red' : '' }">{{ userStore.petList.length }}</span>
          / {{ userStore.getPetCapacity - 3 }}
        </span>
        <el-button size="small" type="success" plain @click="handleArrange">整理</el-button>
        <el-popover placement="top" width="150px" trigger="click" :teleported="false">
          <template #reference>
            <el-button size="small" type="warning" plain style="margin-left: 12px">批量出售</el-button>
          </template>
          <div class="batch-sell affix">
            <el-button size="small" type="primary" plain v-for="item of batchSellQualityList" :key="item.key" @click="handleBatchSell(item)">
              <span :class="item.key">{{ item.name }}及以下</span>
            </el-button>
          </div>
        </el-popover>
      </div>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of petList" :key="item.id">
            <CommonBtn size="small" @done="showItemDetail(item, true)" :style="{ borderColor: petInfo?.id === item.id ? 'red' : '' }">
              <span :class="item.quality" v-html="getPetName(item.monsterId)"></span>
              <el-icon v-show="item.isLock" color="#67c23a" style="position: absolute; top: 0; left: 0; z-index: 1"><Lock /> </el-icon>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog v-model="compareShow" title="魔物比对" width="100%" style="max-width: 500px">
      <template v-if="petInfo">
        <PetCompare :petInfo="petInfo" @close="handleCompareClose" />
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="compareShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.pet-item {
  h4 {
    position: relative;
    .help-info {
      position: absolute;
      top: 0;
      right: 10px;
    }
  }
  .border-wrap {
    padding: 0 10px 10px 10px;
  }
  .action {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    max-height: 45vh;
    overflow: hidden;
    .in-use {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
      button {
        width: 100%;
      }
      .fight {
        margin-bottom: 10px;
      }
      .outside {
        display: flex;
        flex-flow: column nowrap;
        margin-bottom: 10px;
        button {
          margin-bottom: 10px;
          margin-left: 0;
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .detail-info {
      width: calc(50% - 5px);
      height: 100%;
      padding: 0 8px 10px 8px;
      border: 2px var(--fontColor) solid;
      border-radius: 5px;
      margin-left: 10px;
      display: flex;
      flex-flow: column nowrap;
    }
  }
  .pet-list {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    padding: 0;
    .list-action {
      display: flex;
      align-items: center;
      padding: 0 5px;
    }
    .el-row {
      margin-left: 0;
    }
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
  .btn-wrap {
    max-height: 100%;
    overflow: auto;
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .item-btn-wrap {
    & > div {
      margin-bottom: 10px;
    }
  }
}
table {
  width: 100%;
  td {
    width: 60px;
  }
}

.batch-sell {
  display: flex;
  flex-flow: column nowrap;
  button {
    width: 100%;
    margin: 4px 0;
  }
}
</style>
